import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from 'elements/Box';
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ApiRequest } from 'GlobalFunctions';

export default function PurchaseOrderDetails(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { purchase_order_id } = useParams();

  const [order, setOrder] = useState<any>(null);

  useEffect(() => {
    loadDetails();
  }, [purchase_order_id]);

  const loadDetails = async () => {
    setLoading(true);

    await ApiRequest({
      url: `/purchaseorders/details/${purchase_order_id}`,
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          setOrder(response.purchase_order);
        } else {
          enqueueSnackbar('Error al cargar detalles', { variant: 'error' });
        }
        setLoading(false);
      },
    });
  };

  const exportExcel = async () => {
    await ApiRequest({
      url: `/purchaseorders/report/${purchase_order_id}/excel`,
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          window.open(response.fileUrl, '_blank');
        } else {
          enqueueSnackbar('Error al generar reporte', { variant: 'error' });
        }
      },
    });
  };

  const exportPDF = async () => {
    await ApiRequest({
      url: `/purchaseorders/report/${purchase_order_id}/pdf`,
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          window.open(response.pdfUrl, '_blank');
        } else {
          enqueueSnackbar('Error al generar reporte', { variant: 'error' });
        }
      },
    });
  };

  const getVarianceColor = (status: string) => {
    switch (status) {
      case 'match':
        return '#000000'; // Negro
      case 'shortage':
        return '#D32F2F'; // Rojo oscuro
      case 'overage':
        return '#388E3C'; // Verde oscuro
      default:
        return '#000000';
    }
  };

  const getVarianceLabel = (status: string) => {
    switch (status) {
      case 'match':
        return 'OK';
      case 'shortage':
        return 'FALTANTE';
      case 'overage':
        return 'SOBRANTE';
      default:
        return status;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_process':
        return 'En proceso';
      case 'completed':
        return 'Completado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getReceivingStatusLabel = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'No iniciado';
      case 'partial':
        return 'Parcial';
      case 'completed':
        return 'Completado';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '$0';

    const isInteger = amount % 1 === 0;

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: isInteger ? 0 : 2
    }).format(amount).replace('COP', '').trim();
  };

  if (!order) {
    return (
      <div id='PurchaseOrderDetails' className='ModuleContainer'>
        <div className='ModuleWrapper'>
          <Typography variant="h6" align="center" style={{ padding: '40px' }}>
            Cargando...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div id='PurchaseOrderDetails' className='ModuleContainer'>
      <div className='ModuleWrapper'>
        <div className="boxContainerHeader">
          <div className="boxContainerTitle" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <IconButton onClick={() => navigate('/purchaseorders')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <div>
              <h2 className="h2_title">Orden de Compra #{order.invoice_number}</h2>
              <span className="h2_subtitle">{order.supplier_name}</span>
            </div>
          </div>
          <div className="boxContainerActions">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={exportExcel}
              style={{ marginRight: '8px' }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportPDF}
            >
              PDF
            </Button>
          </div>
        </div>

        <Box id="PurchaseOrderDetailsIn" className="box100">
          {/* Header Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  N° Factura
                </Typography>
                <Typography variant="h6">
                  {order.invoice_number}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Fecha de Emisión
                </Typography>
                <Typography variant="body1">
                  {new Date(order.issue_date).toLocaleDateString('es-CO')}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Bodega
                </Typography>
                <Typography variant="body1">
                  {order.warehouse_name || <em style={{ color: '#999' }}>Sin asignar</em>}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(order.total_amount)}
                </Typography>
              </div>
            </div>

            <Divider sx={{ my: 2 }} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Proveedor
                </Typography>
                <Typography variant="body1">
                  {order.supplier_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  NIT: {order.supplier_doc}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Estado
                </Typography>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Chip label={getStatusLabel(order.status)} color="primary" size="small" />
                  <Chip label={getReceivingStatusLabel(order.receiving_status)} color="warning" size="small" />
                </div>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Orden de Compra
                </Typography>
                <Typography variant="body1">
                  {order.order_reference || 'N/A'}
                </Typography>
              </div>
            </div>

            <Divider sx={{ my: 2 }} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Subtotal
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(order.subtotal)}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Impuestos
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(order.tax_amount)}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Creado por
                </Typography>
                <Typography variant="body1">
                  {order.created_by_name}
                </Typography>
              </div>
            </div>
          </Paper>

          {/* Historial de Recepciones */}
          {order.receiveHistory && order.receiveHistory.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Historial de Recepciones
              </Typography>
              <div style={{ marginBottom: '24px' }}>
                {order.receiveHistory.map((receive: any, idx: number) => {
                  const receiveNumber = order.receiveHistory.length - idx; // Numeración descendente

                  return (
                    <Accordion key={receive.receive_id} defaultExpanded={false}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          '&:hover': { backgroundColor: '#eeeeee' }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '16px' }}>
                          <div>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              Recepción #{receiveNumber} - {receive.session_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(receive.receive_date).toLocaleString('es-CO')} • Por: {receive.received_by} • Bodega: {receive.warehouse_name}
                            </Typography>
                          </div>
                         
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        {receive.comments && (
                          <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic', marginBottom: '12px' }}>
                            Comentarios: "{receive.comments}"
                          </Typography>
                        )}

                        {receive.items && receive.items.length > 0 && (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Producto</TableCell>
                                  <TableCell align="right">Cajas</TableCell>
                                  <TableCell align="right">Unidades</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {receive.items.map((item: any) => (
                                  <TableRow key={item.receive_item_id}>
                                    <TableCell>
                                      <Typography variant="body2">{item.product_name}</Typography>
                                      {item.units_per_box > 1 && (
                                        <Typography variant="caption" color="text.secondary">
                                          ({item.units_per_box} unid/caja)
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{item.received_boxes}</TableCell>
                                    <TableCell align="right">{item.received_units}</TableCell>
                                    <TableCell align="right">
                                      <strong>{item.total_received}</strong>
                                    </TableCell>
                                    
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </div>
            </>
          )}

          {/* Items Table */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Items de la Orden (Acumulado)
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="center" colSpan={3}>Esperado</TableCell>
                  <TableCell align="center" colSpan={3}>Recibido</TableCell>
                  <TableCell align="center" colSpan={2}>Diferencia</TableCell>
                  <TableCell align="right">Precio Unit.</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  {/* Esperado */}
                  <TableCell align="right">Cajas</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Total</TableCell>
                  {/* Recibido */}
                  <TableCell align="right">Cajas</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Total</TableCell>
                  {/* Diferencia */}
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items && order.items.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.product_code}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.product_name}
                      </Typography>
                      {item.product_name_db && item.product_name_db !== item.product_name && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          BD: {item.product_name_db}
                        </Typography>
                      )}
                      {item.units_per_box && item.units_per_box > 1 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          ({item.units_per_box} unid/caja)
                        </Typography>
                      )}
                    </TableCell>
                    {/* Esperado */}
                    <TableCell align="right">{item.ordered_boxes || 0}</TableCell>
                    <TableCell align="right">{item.ordered_units || 0}</TableCell>
                    <TableCell align="right">
                      <strong>{item.ordered_quantity || 0}</strong>
                    </TableCell>
                    {/* Recibido */}
                    <TableCell align="right">{item.received_boxes || 0}</TableCell>
                    <TableCell align="right">{item.received_units || 0}</TableCell>
                    <TableCell align="right">
                      <strong>{item.total_received_calc || 0}</strong>
                    </TableCell>
                    {/* Diferencia */}
                    <TableCell
                      align="right"
                      style={{
                        color: getVarianceColor(item.variance_status),
                        fontWeight: 'bold'
                      }}
                    >
                      {item.variance_total > 0 ? '+' : ''}{item.variance_total || 0}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getVarianceLabel(item.variance_status)}
                        size="small"
                        style={{
                          backgroundColor: item.variance_status === 'match' ? '#E0E0E0' :
                            item.variance_status === 'shortage' ? '#FFEBEE' : '#E8F5E9',
                          color: getVarianceColor(item.variance_status),
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.unit_price)}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(item.line_total)}</strong>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
    </div>
  );
}
